import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CalendarBlank, ChartLineUp, Compass, Gift, House, Lightning, Scan, SignOut, Storefront, Users, Wallet, ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";
import { API, fmtErr, mediaUrl } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import MediaUpload from "../../components/MediaUpload";
import { Calendar } from "../../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { formatPlanPrice } from "../../hooks/usePricing";

const NAV = [
  { id: "overview", label: "Overview", icon: House },
  { id: "users", label: "Users", icon: Users },
  { id: "venues", label: "Venues", icon: Storefront },
  { id: "events", label: "Events", icon: CalendarBlank },
  { id: "drops", label: "Drops", icon: Lightning },
  { id: "rewards", label: "Rewards", icon: Gift },
  { id: "payments", label: "Payments", icon: Wallet },
  { id: "scans", label: "Scans", icon: Scan },
];

function Stat({ label, value, accent = "text-white" }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-5">
      <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-500">{label}</div>
      <div className={`mt-2 font-display text-3xl font-black ${accent}`}>{value}</div>
    </div>
  );
}

function datePart(date) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function RewardExpiryPicker({ value, onChange }) {
  const selected = value ? new Date(value) : undefined;
  const time = value?.slice(11, 16) || "23:59";
  const setDate = (date) => {
    if (!date) return;
    onChange(`${datePart(date)}T${time}`);
  };
  const setTime = (nextTime) => {
    const base = value ? value.slice(0, 10) : datePart(new Date());
    onChange(`${base}T${nextTime}`);
  };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" data-testid="reward-expiry-picker"
          className="flex w-full items-center gap-2 rounded-xl border border-white/10 bg-black px-3 py-2.5 text-left text-sm text-zinc-300 outline-none transition-colors focus:border-neon-gold">
          <CalendarBlank size={15} className="text-neon-gold" />
          {value ? new Date(value).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "No expiry"}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto border-white/10 bg-[#0a0a0a] text-white">
        <Calendar mode="single" selected={selected} onSelect={setDate} />
        <div className="mt-3 flex items-center gap-2">
          <input data-testid="reward-expiry-time" type="time" value={time} onChange={(e) => setTime(e.target.value)}
            className="rounded-lg border border-white/10 bg-black px-3 py-2 text-sm outline-none focus:border-neon-gold" />
          <button type="button" data-testid="reward-expiry-clear" onClick={() => onChange("")}
            className="rounded-full border border-white/10 px-3 py-2 text-xs font-bold text-zinc-400 hover:text-white">Clear</button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

const th = "py-2.5 pr-4 text-left text-[11px] uppercase tracking-widest text-zinc-500";
const td = "py-2.5 pr-4 text-sm border-b border-white/5";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("tab") || "overview");
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState([]);
  const [userQuery, setUserQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [venues, setVenues] = useState([]);
  const [events, setEvents] = useState([]);
  const [drops, setDrops] = useState([]);
  const [payments, setPayments] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [pricingForm, setPricingForm] = useState({ promoter_yearly: "49.99", venue_yearly: "99", promoter_monthly: "14.99", venue_monthly: "29.99" });
  const [scans, setScans] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [rewardForm, setRewardForm] = useState({ title: "", description: "", point_cost: 500, quantity_limit: "", expires_at: "", venue_id: "", image_url: "" });
  const [apiKeys, setApiKeys] = useState([]);
  const [apiKeyLabel, setApiKeyLabel] = useState("");
  const [newApiKey, setNewApiKey] = useState(null);
  const navigate = useNavigate();

  const loadApiKeys = () => API.get("/admin/api-keys")
    .then((r) => setApiKeys(r.data)).catch((e) => toast.error(fmtErr(e)));

  const createApiKey = async (e) => {
    e.preventDefault();
    try {
      const { data } = await API.post("/admin/api-keys", { label: apiKeyLabel });
      setNewApiKey(data.api_key);
      setApiKeyLabel("");
      loadApiKeys();
      toast.success("API key created — copy it now, it won't be shown again");
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const revokeApiKey = async (k) => {
    try {
      await API.delete(`/admin/api-keys/${k.id}`);
      toast.success(`Revoked "${k.label}"`);
      loadApiKeys();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const loadUsers = () => API.get(`/admin/users?q=${encodeURIComponent(userQuery)}&role=${roleFilter}`)
    .then((r) => setUsers(r.data)).catch((e) => toast.error(fmtErr(e)));
  const loadRewards = () => API.get("/admin/rewards")
    .then((r) => setRewards(r.data)).catch((e) => toast.error(fmtErr(e)));
  const loadPricing = () => API.get("/payments/pricing")
    .then((r) => {
      setPricing(r.data);
      setPricingForm({
        promoter_yearly: String(r.data.promoter_yearly.amount),
        venue_yearly: String(r.data.venue_yearly.amount),
        promoter_monthly: String(r.data.promoter_monthly.amount),
        venue_monthly: String(r.data.venue_monthly.amount),
      });
      if (r.data.launch_offer) {
        setLaunchForm({ cap: String(r.data.launch_offer.cap), amount: String(r.data.launch_offer.amount) });
      }
    }).catch((e) => toast.error(fmtErr(e)));

  useEffect(() => { API.get("/admin/overview").then((r) => setOverview(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    API.get("/admin/alerts").then((r) => setAlerts(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (tab === "users") loadUsers();
    if (tab === "venues") API.get("/admin/venues").then((r) => setVenues(r.data)).catch(() => {});
    if (tab === "events") API.get("/admin/events").then((r) => setEvents(r.data)).catch(() => {});
    if (tab === "drops") API.get("/admin/drops").then((r) => setDrops(r.data)).catch(() => {});
    if (tab === "rewards") {
      loadRewards();
      API.get("/admin/venues").then((r) => setVenues(r.data)).catch(() => {});
    }
    if (tab === "payments") {
      API.get("/admin/payments").then((r) => setPayments(r.data)).catch(() => {});
      loadPricing();
      loadApiKeys();
    }
    if (tab === "scans") API.get("/admin/scans").then((r) => setScans(r.data)).catch(() => {});
  }, [tab, roleFilter]);

  const changeRole = async (u, role) => {
    try {
      await API.post(`/admin/users/${u.id}/role`, { role });
      toast.success(`${u.handle || u.email} is now ${role.toUpperCase()}`);
      loadUsers();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const suspend = async (u) => {    try {
      const { data } = await API.post(`/admin/users/${u.id}/suspend`);
      toast.success(data.suspended ? `${u.handle} suspended` : `${u.handle} reinstated`);
      loadUsers();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Delete ${u.email} and their passes/favorites?`)) return;
    try {
      await API.delete(`/admin/users/${u.id}`);
      toast.success("User deleted");
      loadUsers();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const removeEvent = async (e) => {
    if (!window.confirm(`Delete event "${e.title}"?`)) return;
    await API.delete(`/admin/events/${e.id}`).then(() => {
      toast.success("Event deleted");
      setEvents((s) => s.filter((x) => x.id !== e.id));
    }).catch((err) => toast.error(fmtErr(err)));
  };

  const removeDrop = async (d) => {
    await API.delete(`/admin/drops/${d.id}`).then(() => {
      toast.success("Drop removed");
      setDrops((s) => s.filter((x) => x.id !== d.id));
    }).catch((err) => toast.error(fmtErr(err)));
  };

  const createReward = async (e) => {
    e.preventDefault();
    try {
      await API.post("/admin/rewards", {
        ...rewardForm,
        point_cost: Number(rewardForm.point_cost),
        quantity_limit: rewardForm.quantity_limit ? Number(rewardForm.quantity_limit) : null,
        expires_at: rewardForm.expires_at || null,
        venue_id: rewardForm.venue_id || null,
        image_url: rewardForm.image_url || null,
      });
      toast.success("NITEPASS reward is live");
      setRewardForm({ title: "", description: "", point_cost: 500, quantity_limit: "", expires_at: "", venue_id: "", image_url: "" });
      loadRewards();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const toggleReward = async (reward) => {
    try {
      const { data } = await API.post(`/admin/rewards/${reward.id}/toggle`);
      toast.success(data.active ? "Reward activated" : "Reward paused");
      loadRewards();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const removeReward = async (reward) => {
    if (!window.confirm(`Delete reward "${reward.title}"?`)) return;
    try {
      await API.delete(`/admin/rewards/${reward.id}`);
      toast.success("Reward deleted");
      setRewards((s) => s.filter((r) => r.id !== reward.id));
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const savePricing = async (e) => {
    e.preventDefault();
    try {
      const { data } = await API.patch("/payments/pricing", {
        promoter_yearly: Number(pricingForm.promoter_yearly),
        venue_yearly: Number(pricingForm.venue_yearly),
        promoter_monthly: Number(pricingForm.promoter_monthly),
        venue_monthly: Number(pricingForm.venue_monthly),
      });
      setPricing(data);
      toast.success("Pricing updated for new checkouts");
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const [launchForm, setLaunchForm] = useState({ cap: "20", amount: "49" });

  const ALERT_LABELS = [["new_user", "New promoters"], ["new_venue", "New venues"], ["new_guest", "New guests"], ["first_payment", "New subscriptions"]];
  const [alerts, setAlerts] = useState({ new_user: true, new_venue: true, new_guest: true, first_payment: true });

  const toggleAlert = async (key) => {
    try {
      const { data } = await API.patch("/admin/alerts", { [key]: !alerts[key] });
      setAlerts(data);
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const saveLaunchOffer = async (e) => {
    e.preventDefault();
    try {
      const { data } = await API.patch("/payments/launch-offer", {
        cap: Number(launchForm.cap), amount: Number(launchForm.amount),
      });
      setPricing((p) => ({ ...p, launch_offer: data }));
      toast.success(data.active
        ? `Founders offer updated — cap now ${data.cap} per city`
        : "Founders offer updated — offer hidden");
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const waiveFee = async (target) => {
    const plan = target.role === "venue" ? "venue_yearly" : "promoter_yearly";
    try {
      await API.post(`/admin/users/${target.id}/waive-fee`, { plan });
      toast.success(`${target.handle} fee waived for one year`);
      loadUsers();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const removeFeeWaiver = async (target) => {
    try {
      await API.delete(`/admin/users/${target.id}/waive-fee`);
      toast.success(`Fee waiver removed for ${target.handle}`);
      loadUsers();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const toggleFounder = async (target) => {
    const grant = !target.subscription?.founder;
    try {
      await API.post(`/admin/users/${target.id}/founder`, { founder: grant });
      toast.success(grant ? `Founder badge granted to ${target.handle}` : `Founder badge removed from ${target.handle}`);
      loadUsers();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const o = overview;

  return (
    <div data-testid="admin-dashboard" className="flex min-h-screen bg-[#050505] text-white"
      style={{ paddingTop: "env(safe-area-inset-top)" }}>
      <aside className="glass flex w-16 flex-col items-center gap-2 border-y-0 border-l-0 py-6 md:w-56 md:items-stretch md:px-4">
        <div className="mb-6 px-2">
          <img src="/logo.png" alt="NITEPASS" className="h-10 w-auto md:h-12" />
          <div className="mt-1 hidden text-[10px] font-bold uppercase tracking-[0.25em] text-neon-gold md:block">Admin</div>
        </div>
        {NAV.map((n) => (
          <button key={n.id} data-testid={`admin-nav-${n.id}`} onClick={() => setTab(n.id)}
            className={`flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-bold transition-colors duration-200 ${tab === n.id ? "bg-neon-gold/15 text-neon-gold" : "text-zinc-500 hover:text-white"}`}>
            <n.icon size={20} /> <span className="hidden md:inline">{n.label}</span>
          </button>
        ))}
        <button data-testid="admin-guest-app" onClick={() => navigate("/app")}
          className="mt-auto flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-bold text-zinc-500 hover:text-neon-gold">
          <Compass size={20} /> <span className="hidden md:inline">Guest App</span>
        </button>
        <button data-testid="admin-logout" onClick={async () => { await logout(); navigate("/"); }}
          className="flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-bold text-zinc-600 hover:text-red-400">
          <SignOut size={20} /> <span className="hidden md:inline">Log out</span>
        </button>
      </aside>

      <main className="flex-1 overflow-x-hidden p-4 md:p-8">
        <div className="flex items-center gap-3">
          <button data-testid="admin-back-btn" onClick={() => navigate("/")}
            className="glass rounded-full p-2 text-zinc-400 transition-colors hover:text-neon-gold">
            <ArrowLeft size={16} />
          </button>
          <div className="neon-overline !text-neon-gold">Platform Command</div>
        </div>
        <h1 className="mt-1 font-display text-2xl font-black">Admin Dashboard</h1>

        {tab === "overview" && o && (
          <div className="mt-6" data-testid="admin-overview">
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              <Stat label="Total Users" value={o.total_users} />
              <Stat label="Guests" value={o.users_by_role.guest || 0} accent="text-neon-violet" />
              <Stat label="Promoters" value={o.users_by_role.promoter || 0} accent="text-neon-violet" />
              <Stat label="Venue Accounts" value={o.users_by_role.venue || 0} accent="text-neon-violet" />
              <Stat label="Venue Listings" value={o.venues} />
              <Stat label="Events Tonight" value={o.events_tonight} accent="text-neon-gold" />
              <Stat label="Check-ins Tonight" value={o.checkins_tonight} accent="text-neon-green" />
              <Stat label="Active Drops" value={o.active_drops} accent="text-neon-green" />
              <Stat label="Entry Revenue" value={`$${o.entry_revenue.toLocaleString()}`} accent="text-neon-gold" />
              <Stat label="Entry Payments" value={o.entry_count} />
              <Stat label="Event Fee Revenue" value={`$${(o.event_fee_revenue || 0).toLocaleString()}`} accent="text-neon-gold" />
              <Stat label="Subscription Revenue" value={`$${o.subscription_revenue.toLocaleString()}`} accent="text-neon-gold" />
              <Stat label="Wallet Liability" value={`$${o.wallet_liability.toLocaleString()}`} accent="text-neon-yellow" />
            </div>
            <div className="mt-6 rounded-2xl border border-white/10 bg-[#0a0a0a] p-5">
              <div className="mb-3 text-[11px] font-bold uppercase tracking-widest text-zinc-500">Latest signups</div>
              <div className="space-y-2" data-testid="recent-signups">
                {o.recent_users.map((u) => (
                  <div key={u.id} className="flex items-center justify-between text-sm">
                    <span><span className="font-bold">{u.name}</span> <span className="text-xs text-zinc-500">{u.email}</span></span>
                    <span className="rounded-full bg-white/5 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-zinc-400">{u.role}</span>
                  </div>
                ))}
                {o.recent_users.length === 0 && <p className="text-sm text-zinc-500">No signups yet.</p>}
              </div>
            </div>
          </div>
        )}

        {tab === "users" && (
          <div className="mt-6" data-testid="admin-users">
            <div className="flex flex-wrap gap-2">
              <input data-testid="user-search-input" value={userQuery} onChange={(e) => setUserQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && loadUsers()}
                placeholder="Search name, email, handle..."
                className="w-64 rounded-xl border border-white/10 bg-black px-4 py-2.5 text-sm outline-none focus:border-neon-gold" />
              <button onClick={loadUsers} className="rounded-full bg-neon-gold px-4 py-2 text-xs font-black text-black">Search</button>
              {["", "guest", "promoter", "venue", "admin"].map((r) => (
                <button key={r} data-testid={`role-filter-${r || "all"}`} onClick={() => setRoleFilter(r)}
                  className={`rounded-full border px-3.5 py-2 text-xs font-bold transition-colors duration-150 ${roleFilter === r ? "border-neon-gold bg-neon-gold/15 text-neon-gold" : "border-white/10 text-zinc-500"}`}>
                  {r || "All"}
                </button>
              ))}
            </div>
            <div className="mt-4 overflow-x-auto">
              <table className="w-full min-w-[820px]">
                <thead><tr className="border-b border-white/10">
                  <th className={th}>User</th><th className={th}>Email</th><th className={th}>Role</th>
                  <th className={th}>Subscription</th><th className={th}>Points</th><th className={th}>Status</th><th className={th}>Actions</th>
                </tr></thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} data-testid={`user-row-${u.id}`}>
                      <td className={td}><span className="font-bold">{u.name}</span> <span className="text-xs text-neon-violet">{u.handle}</span></td>
                      <td className={`${td} text-zinc-400`}>{u.email}</td>
                      <td className={td}>
                        {u.is_main_admin ? (
                          <span className="rounded-full border border-neon-gold/40 bg-neon-gold/10 px-2 py-0.5 text-[10px] font-black uppercase text-neon-gold">Main Admin</span>
                        ) : user?.is_main_admin && u.id !== user.id ? (
                          <select data-testid={`role-select-${u.id}`} value={u.role} onChange={(e) => changeRole(u, e.target.value)}
                            className="rounded-lg border border-white/10 bg-black px-2 py-1 text-[11px] font-bold uppercase outline-none transition-colors focus:border-neon-gold">
                            {["guest", "promoter", "venue", "admin"].map((r) => <option key={r} value={r}>{r}</option>)}
                          </select>
                        ) : (
                          <span className="rounded-full bg-white/5 px-2 py-0.5 text-[10px] font-bold uppercase">{u.role}</span>
                        )}
                      </td>
                      <td className={`${td} text-xs`}>
                        {u.subscription?.status === "active"
                          ? <span className="text-neon-green">{u.subscription.plan}{u.subscription.fee_waived ? " · waived" : ""}</span>
                          : <span className="text-zinc-600">—</span>}
                      </td>
                      <td className={`${td} text-neon-yellow`}>{u.nite_points || 0}</td>
                      <td className={td}>{u.suspended ? <span className="text-xs font-bold text-red-400">SUSPENDED</span> : <span className="text-xs text-zinc-500">active</span>}</td>
                      <td className={td}>
                        {u.role !== "admin" && (
                          <div className="flex flex-wrap gap-2">
                            {["promoter", "venue"].includes(u.role) && (
                              <button data-testid={`waive-fee-${u.id}`} onClick={() => u.subscription?.fee_waived ? removeFeeWaiver(u) : waiveFee(u)}
                                className={`rounded-full border px-3 py-1 text-[11px] font-bold ${u.subscription?.fee_waived ? "border-neon-green/40 text-neon-green" : "border-neon-violet/40 text-neon-violet"}`}>
                                {u.subscription?.fee_waived ? "Remove Waiver" : "Waive Fee"}
                              </button>
                            )}
                            {u.role === "venue" && u.subscription?.status === "active" && (
                              <button data-testid={`founder-toggle-${u.id}`} onClick={() => toggleFounder(u)}
                                className={`rounded-full border px-3 py-1 text-[11px] font-bold ${u.subscription?.founder ? "border-neon-gold/50 bg-neon-gold/10 text-neon-gold" : "border-white/15 text-zinc-400 hover:text-neon-gold"}`}>
                                {u.subscription?.founder ? "★ Founder" : "Make Founder"}
                              </button>
                            )}
                            <button data-testid={`suspend-${u.id}`} onClick={() => suspend(u)}
                              className={`rounded-full border px-3 py-1 text-[11px] font-bold ${u.suspended ? "border-neon-green/40 text-neon-green" : "border-neon-yellow/40 text-neon-yellow"}`}>
                              {u.suspended ? "Reinstate" : "Suspend"}
                            </button>
                            <button data-testid={`delete-${u.id}`} onClick={() => removeUser(u)}
                              className="rounded-full border border-red-500/40 px-3 py-1 text-[11px] font-bold text-red-400">Delete</button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {users.length === 0 && <p className="py-8 text-center text-sm text-zinc-500">No users found.</p>}
            </div>
          </div>
        )}

        {tab === "venues" && (
          <div className="mt-6 overflow-x-auto" data-testid="admin-venues">
            <table className="w-full min-w-[760px]">
              <thead><tr className="border-b border-white/10">
                <th className={th}>Venue</th><th className={th}>Owner</th><th className={th}>Cover</th>
                <th className={th}>Events</th><th className={th}>Check-ins</th><th className={th}>Wallet</th>
              </tr></thead>
              <tbody>
                {venues.map((v) => (
                  <tr key={v.id}>
                    <td className={`${td} font-bold`}>{v.name}</td>
                    <td className={`${td} text-xs text-zinc-400`}>{v.owner_email || "—"}</td>
                    <td className={td}>${v.cover}</td>
                    <td className={td}>{v.events_count}</td>
                    <td className={`${td} text-neon-green`}>{v.checkins}</td>
                    <td className={`${td} font-bold text-neon-gold`}>${v.wallet_balance.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {venues.length === 0 && <p className="py-8 text-center text-sm text-zinc-500">No venues yet.</p>}
          </div>
        )}

        {tab === "events" && (
          <div className="mt-6 overflow-x-auto" data-testid="admin-events">
            <table className="w-full min-w-[820px]">
              <thead><tr className="border-b border-white/10">
                <th className={th}>Event</th><th className={th}>Venue</th><th className={th}>Promoter</th>
                <th className={th}>Cover</th><th className={th}>Going</th><th className={th}>Checked In</th><th className={th}></th>
              </tr></thead>
              <tbody>
                {events.map((e) => (
                  <tr key={e.id}>
                    <td className={`${td} font-bold`}>{e.title} {e.tonight && <span className="ml-1 rounded-full bg-neon-green/15 px-2 py-0.5 text-[9px] font-black text-neon-green">TONIGHT</span>}</td>
                    <td className={`${td} text-zinc-400`}>{e.venue_name}</td>
                    <td className={`${td} text-neon-violet`}>{e.promoter_handle || "House"}</td>
                    <td className={td}>${e.cover}</td>
                    <td className={td}>{e.going_count}</td>
                    <td className={`${td} text-neon-green`}>{e.checked_in}</td>
                    <td className={td}><button data-testid={`delete-event-${e.id}`} onClick={() => removeEvent(e)} className="rounded-full border border-red-500/40 px-3 py-1 text-[11px] font-bold text-red-400">Delete</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {events.length === 0 && <p className="py-8 text-center text-sm text-zinc-500">No events yet.</p>}
          </div>
        )}

        {tab === "drops" && (
          <div className="mt-6 grid gap-3 md:grid-cols-2" data-testid="admin-drops">
            {drops.map((d) => (
              <div key={d.id} className={`rounded-2xl border p-4 ${d.live ? "border-neon-green/40 bg-neon-green/5" : "border-white/10 bg-white/[0.02] opacity-60"}`}>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-black uppercase tracking-widest text-neon-green">{d.live ? "LIVE" : "EXPIRED"}</span>
                  <button data-testid={`delete-drop-${d.id}`} onClick={() => removeDrop(d)} className="rounded-full border border-red-500/40 px-3 py-1 text-[10px] font-bold text-red-400">Remove</button>
                </div>
                <div className="mt-1 font-display text-sm font-bold">{d.perk}</div>
                <div className="mt-0.5 text-xs text-zinc-500">{d.venue_name} · {d.claimed}/{d.total} claimed</div>
              </div>
            ))}
            {drops.length === 0 && <p className="py-8 text-sm text-zinc-500">No drops yet.</p>}
          </div>
        )}

        {tab === "rewards" && (
          <div className="mt-6 grid gap-6 lg:grid-cols-[420px_1fr]" data-testid="admin-rewards">
            <form onSubmit={createReward} className="rounded-3xl border border-neon-gold/30 bg-[#0d0903] p-6">
              <div className="flex items-center gap-2 font-display text-lg font-black"><Gift size={20} className="text-neon-gold" /> Add NITEPASS Reward</div>
              <p className="mt-1 text-xs text-zinc-500">Users claim these with NITE POINTS in the guest Rewards tab.</p>
              <input data-testid="reward-title-input" required placeholder="Reward title" value={rewardForm.title}
                onChange={(e) => setRewardForm({ ...rewardForm, title: e.target.value })}
                className="mt-4 w-full rounded-xl border border-white/10 bg-black px-4 py-2.5 text-sm outline-none focus:border-neon-gold" />
              <textarea data-testid="reward-description-input" placeholder="Description / redemption instructions" value={rewardForm.description}
                onChange={(e) => setRewardForm({ ...rewardForm, description: e.target.value })}
                className="mt-3 min-h-20 w-full rounded-xl border border-white/10 bg-black px-4 py-2.5 text-sm outline-none focus:border-neon-gold" />
              <div className="mt-3 grid grid-cols-2 gap-3">
                <input data-testid="reward-cost-input" type="number" min="1" required placeholder="Point cost" value={rewardForm.point_cost}
                  onChange={(e) => setRewardForm({ ...rewardForm, point_cost: e.target.value })}
                  className="rounded-xl border border-white/10 bg-black px-4 py-2.5 text-sm outline-none focus:border-neon-gold" />
                <input data-testid="reward-quantity-input" type="number" min="1" placeholder="Quantity (blank = unlimited)" value={rewardForm.quantity_limit}
                  onChange={(e) => setRewardForm({ ...rewardForm, quantity_limit: e.target.value })}
                  className="rounded-xl border border-white/10 bg-black px-4 py-2.5 text-sm outline-none focus:border-neon-gold" />
              </div>
              <div className="mt-3 grid grid-cols-2 gap-3">
                <select data-testid="reward-venue-select" value={rewardForm.venue_id}
                  onChange={(e) => setRewardForm({ ...rewardForm, venue_id: e.target.value })}
                  className="rounded-xl border border-white/10 bg-black px-3 py-2.5 text-sm outline-none focus:border-neon-gold">
                  <option value="">Any venue</option>
                  {venues.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
                <RewardExpiryPicker value={rewardForm.expires_at}
                  onChange={(next) => setRewardForm({ ...rewardForm, expires_at: next })} />
              </div>
              <div className="mt-3">
                <MediaUpload label="Upload reward image" onUploaded={(m) => setRewardForm((f) => ({ ...f, image_url: m?.url || "" }))} />
              </div>
              <button data-testid="reward-submit-btn" type="submit"
                className="mt-4 w-full rounded-full bg-neon-gold py-3 font-display text-sm font-black text-black shadow-glow-gold">
                Publish Reward
              </button>
            </form>

            <div className="space-y-3" data-testid="admin-rewards-list">
              {rewards.map((reward) => (
                <div key={reward.id} className={`rounded-2xl border p-4 ${reward.active ? "border-neon-gold/30 bg-neon-gold/5" : "border-white/10 bg-white/[0.02] opacity-60"}`}>
                  <div className="flex gap-4">
                    {reward.image_url && <img src={mediaUrl(reward.image_url)} alt="" className="h-20 w-20 rounded-xl object-cover" />}
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="font-display text-sm font-black">{reward.title}</div>
                        <span className="rounded-full bg-neon-yellow/10 px-3 py-1 text-xs font-black text-neon-yellow">{reward.point_cost} PTS</span>
                      </div>
                      {reward.description && <p className="mt-1 text-xs text-zinc-400">{reward.description}</p>}
                      <div className="mt-2 flex flex-wrap gap-2 text-[10px] font-bold uppercase tracking-wider text-zinc-500">
                        <span>{reward.venue_name || "Any venue"}</span>
                        <span>· {reward.quantity_limit === null ? "Unlimited" : `${reward.quantity_claimed}/${reward.quantity_limit} claimed`}</span>
                        {reward.expires_at && <span>· Ends {new Date(reward.expires_at).toLocaleString()}</span>}
                      </div>
                      <div className="mt-3 flex gap-2">
                        <button data-testid={`toggle-reward-${reward.id}`} onClick={() => toggleReward(reward)}
                          className={`rounded-full border px-3 py-1.5 text-[11px] font-bold ${reward.active ? "border-neon-yellow/40 text-neon-yellow" : "border-neon-green/40 text-neon-green"}`}>
                          {reward.active ? "Pause" : "Activate"}
                        </button>
                        <button data-testid={`delete-reward-${reward.id}`} onClick={() => removeReward(reward)}
                          className="rounded-full border border-red-500/40 px-3 py-1.5 text-[11px] font-bold text-red-400">Delete</button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {rewards.length === 0 && <p className="py-8 text-center text-sm text-zinc-500">No NITEPASS rewards yet.</p>}
            </div>
          </div>
        )}

        {tab === "payments" && payments && (
          <div className="mt-6" data-testid="admin-payments">
            {pricing && (
              <form onSubmit={savePricing} className="mb-6 rounded-3xl border border-neon-gold/30 bg-[#0d0903] p-5" data-testid="pricing-manager">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="font-display text-lg font-black">Subscription Pricing</div>
                    <div className="mt-1 text-xs text-zinc-500">Changes apply to new Stripe checkouts only; existing subscribers keep their current renewal price.</div>
                  </div>
                  <button data-testid="save-pricing-btn" className="rounded-full bg-neon-gold px-5 py-2 text-xs font-black text-black shadow-glow-gold">Save Prices</button>
                </div>
                <div className="mt-4 grid gap-3 md:grid-cols-2">
                  <label className="text-xs font-bold uppercase tracking-widest text-zinc-500">Promoter yearly
                    <div className="mt-1 flex items-center rounded-xl border border-white/10 bg-black px-3">
                      <span className="text-neon-gold">$</span>
                      <input data-testid="promoter-yearly-price-input" type="number" min="1" step="0.01" value={pricingForm.promoter_yearly}
                        onChange={(e) => setPricingForm({ ...pricingForm, promoter_yearly: e.target.value })}
                        className="w-full bg-transparent px-2 py-3 text-sm outline-none" />
                    </div>
                    <span className="mt-1 block text-[10px] normal-case text-zinc-600">Current: {formatPlanPrice(pricing.promoter_yearly)}/year</span>
                  </label>
                  <label className="text-xs font-bold uppercase tracking-widest text-zinc-500">Promoter monthly
                    <div className="mt-1 flex items-center rounded-xl border border-white/10 bg-black px-3">
                      <span className="text-neon-gold">$</span>
                      <input data-testid="promoter-monthly-price-input" type="number" min="1" step="0.01" value={pricingForm.promoter_monthly}
                        onChange={(e) => setPricingForm({ ...pricingForm, promoter_monthly: e.target.value })}
                        className="w-full bg-transparent px-2 py-3 text-sm outline-none" />
                    </div>
                    <span className="mt-1 block text-[10px] normal-case text-zinc-600">Current: {formatPlanPrice(pricing.promoter_monthly)}/month</span>
                  </label>
                  <label className="text-xs font-bold uppercase tracking-widest text-zinc-500">Venue yearly
                    <div className="mt-1 flex items-center rounded-xl border border-white/10 bg-black px-3">
                      <span className="text-neon-gold">$</span>
                      <input data-testid="venue-yearly-price-input" type="number" min="1" step="0.01" value={pricingForm.venue_yearly}
                        onChange={(e) => setPricingForm({ ...pricingForm, venue_yearly: e.target.value })}
                        className="w-full bg-transparent px-2 py-3 text-sm outline-none" />
                    </div>
                    <span className="mt-1 block text-[10px] normal-case text-zinc-600">Current: {formatPlanPrice(pricing.venue_yearly)}/year</span>
                  </label>
                  <label className="text-xs font-bold uppercase tracking-widest text-zinc-500">Venue monthly
                    <div className="mt-1 flex items-center rounded-xl border border-white/10 bg-black px-3">
                      <span className="text-neon-gold">$</span>
                      <input data-testid="venue-monthly-price-input" type="number" min="1" step="0.01" value={pricingForm.venue_monthly}
                        onChange={(e) => setPricingForm({ ...pricingForm, venue_monthly: e.target.value })}
                        className="w-full bg-transparent px-2 py-3 text-sm outline-none" />
                    </div>
                    <span className="mt-1 block text-[10px] normal-case text-zinc-600">Current: {formatPlanPrice(pricing.venue_monthly)}/month</span>
                  </label>
                </div>
              </form>
            )}
            <div className="mb-6 rounded-3xl border border-white/10 bg-[#0a0a0a] p-5" data-testid="admin-alerts-panel">
              <div className="mb-1 text-xs font-bold uppercase tracking-widest text-zinc-500">Signup Alerts</div>
              <p className="mb-3 text-[11px] text-zinc-600">
                Push + in-app notification to all admins when someone joins. Toggle per category.
              </p>
              <div className="flex flex-wrap gap-2">
                {ALERT_LABELS.map(([key, label]) => (
                  <button key={key} data-testid={`alert-toggle-${key}`} onClick={() => toggleAlert(key)}
                    className={`rounded-full border px-4 py-2 text-xs font-bold transition-colors duration-200 ${alerts[key] ? "border-neon-gold/50 bg-neon-gold/10 text-neon-gold" : "border-white/10 text-zinc-600"}`}>
                    {label} {alerts[key] ? "ON" : "OFF"}
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-6 rounded-3xl border border-neon-gold/30 bg-[#0a0905] p-5" data-testid="partner-api-manager">
              <div className="mb-1 text-xs font-bold uppercase tracking-widest text-neon-gold">Parent Company API</div>
              <p className="mb-3 text-[11px] leading-relaxed text-zinc-600">
                Read-only stats API for the parent company's admin panel. Base URL <span className="font-mono text-zinc-400">{window.location.origin}/api/partner</span> with header
                <span className="font-mono text-zinc-400"> X-API-Key</span>. Endpoints: <span className="font-mono text-zinc-400">/stats/overview</span>, <span className="font-mono text-zinc-400">/stats/daily?days=30</span>, <span className="font-mono text-zinc-400">/stats/venues</span>.
              </p>
              <form onSubmit={createApiKey} className="flex gap-2">
                <input data-testid="api-key-label-input" required value={apiKeyLabel} maxLength={60}
                  onChange={(e) => setApiKeyLabel(e.target.value)} placeholder="Key label (e.g. Parent dashboard)"
                  className="flex-1 rounded-xl border border-white/10 bg-black px-3 py-2.5 text-sm outline-none focus:border-neon-gold" />
                <button data-testid="api-key-create-btn" className="rounded-full bg-neon-gold px-5 py-2 text-xs font-black text-black">Create Key</button>
              </form>
              {newApiKey && (
                <div className="mt-3 rounded-xl border border-neon-gold/40 bg-black p-3" data-testid="api-key-reveal">
                  <div className="mb-1 text-[10px] font-black uppercase tracking-widest text-neon-gold">Copy now — shown once</div>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 break-all font-mono text-xs text-zinc-300">{newApiKey}</code>
                    <button type="button" data-testid="api-key-copy-btn"
                      onClick={() => { navigator.clipboard?.writeText(newApiKey); toast.success("Copied"); }}
                      className="shrink-0 rounded-full border border-neon-gold/50 px-3 py-1 text-[10px] font-bold text-neon-gold">Copy</button>
                  </div>
                </div>
              )}
              <div className="mt-3 space-y-1.5" data-testid="api-key-list">
                {apiKeys.length === 0 && <p className="text-[11px] text-zinc-600">No keys yet.</p>}
                {apiKeys.map((k) => (
                  <div key={k.id} data-testid={`api-key-row-${k.id}`}
                    className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2">
                    <div className="min-w-0">
                      <span className="text-xs font-bold">{k.label}</span>
                      <span className="ml-2 font-mono text-[10px] text-zinc-500">{k.prefix}…</span>
                      <span className="ml-2 text-[10px] text-zinc-600">
                        {k.active ? (k.last_used_at ? `last used ${k.last_used_at.slice(0, 10)}` : "never used") : "REVOKED"}
                      </span>
                    </div>
                    {k.active && (
                      <button data-testid={`api-key-revoke-${k.id}`} onClick={() => revokeApiKey(k)}
                        className="ml-3 shrink-0 rounded-full border border-red-500/40 px-3 py-1 text-[10px] font-bold text-red-400">Revoke</button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {pricing?.launch_offer && (
              <form onSubmit={saveLaunchOffer} className="mb-6 rounded-3xl border border-neon-violet/30 bg-[#0a0612] p-5" data-testid="launch-offer-manager">                <div className="mb-1 text-xs font-bold uppercase tracking-widest text-neon-violet">Founders Launch Offer</div>
                <p className="mb-3 text-[11px] leading-relaxed text-zinc-600">
                  First {pricing.launch_offer.cap} venue yearly signups per city lock in the founders price for life.
                  {pricing.launch_offer.total_redeemed > 0
                    ? ` ${pricing.launch_offer.total_redeemed} redeemed worldwide.`
                    : " None redeemed yet."}
                  {" "}Lower the cap to end the offer in cities that haven't filled it.
                </p>
                {(pricing.launch_offer.cities || []).length > 0 && (
                  <div className="mb-3 flex flex-wrap gap-1.5" data-testid="launch-offer-cities">
                    {pricing.launch_offer.cities.map((c) => (
                      <span key={c.city} data-testid={`launch-city-${c.city.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        className={`rounded-full border px-2.5 py-1 text-[10px] font-bold ${c.active ? "border-neon-gold/40 text-neon-gold" : "border-white/10 text-zinc-600"}`}>
                        {c.city}: {c.redeemed}/{pricing.launch_offer.cap}{c.active ? "" : " · full"}
                      </span>
                    ))}
                  </div>
                )}
                <div className="grid gap-3 md:grid-cols-2">
                  <label className="text-xs font-bold uppercase tracking-widest text-zinc-500">Spots (cap)
                    <input data-testid="launch-cap-input" type="number" min="1" value={launchForm.cap}
                      onChange={(e) => setLaunchForm({ ...launchForm, cap: e.target.value })}
                      className="mt-1 w-full rounded-xl border border-white/10 bg-black px-3 py-3 text-sm outline-none focus:border-neon-violet" />
                  </label>
                  <label className="text-xs font-bold uppercase tracking-widest text-zinc-500">Founders yearly price
                    <div className="mt-1 flex items-center rounded-xl border border-white/10 bg-black px-3">
                      <span className="text-neon-violet">$</span>
                      <input data-testid="launch-price-input" type="number" min="1" step="0.01" value={launchForm.amount}
                        onChange={(e) => setLaunchForm({ ...launchForm, amount: e.target.value })}
                        className="w-full bg-transparent px-2 py-3 text-sm outline-none" />
                    </div>
                  </label>
                </div>
                <div className="mt-4 flex justify-end">
                  <button data-testid="save-launch-offer-btn" className="rounded-full bg-neon-violet px-5 py-2 text-xs font-black text-black">Save Offer</button>
                </div>
              </form>
            )}
            <div className="grid gap-6 lg:grid-cols-2">
            <div className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-5">
              <div className="mb-3 text-[11px] font-bold uppercase tracking-widest text-zinc-500">Payment transactions</div>
              <div className="space-y-2">
                {payments.transactions.map((t) => (
                  <div key={t.session_id} className="flex items-center justify-between rounded-xl border border-white/5 bg-white/[0.02] px-3 py-2.5 text-xs">
                    <div>
                      <span className="font-bold">{t.kind === "entry" ? "Entry" : t.lookup_key?.replace(/_/g, " ")}</span>
                      <span className="ml-2 text-zinc-500">{t.user_email || ""}</span>
                    </div>
                    <div className="text-right">
                      <span className="font-black text-neon-gold">${(t.amount / 100).toFixed(2)}</span>
                      <div className={`text-[10px] font-bold ${t.payment_status === "paid" ? "text-neon-green" : "text-zinc-500"}`}>{t.payment_status}</div>
                    </div>
                  </div>
                ))}
                {payments.transactions.length === 0 && <p className="py-4 text-sm text-zinc-500">No transactions yet.</p>}
              </div>
            </div>
            <div className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-5">
              <div className="mb-3 text-[11px] font-bold uppercase tracking-widest text-zinc-500">Wallet ledger (all accounts)</div>
              <div className="space-y-2">
                {payments.ledger.map((l) => (
                  <div key={l.id} className="flex items-center justify-between rounded-xl border border-white/5 bg-white/[0.02] px-3 py-2.5 text-xs">
                    <div>
                      <span className="font-bold">{l.note}</span>
                      <span className="ml-2 text-[10px] uppercase tracking-wider text-zinc-500">{l.owner_type} · {l.category.replace(/_/g, " ")}</span>
                    </div>
                    <span className={`font-black ${l.direction === "credit" ? "text-neon-green" : "text-zinc-400"}`}>
                      {l.direction === "credit" ? "+" : "−"}${l.amount.toFixed(2)}
                    </span>
                  </div>
                ))}
                {payments.ledger.length === 0 && <p className="py-4 text-sm text-zinc-500">No wallet activity yet.</p>}
              </div>
            </div>
            </div>
          </div>
        )}

        {tab === "scans" && (
          <div className="mt-6 space-y-2" data-testid="admin-scans">
            {scans.map((s) => (
              <div key={s.id} className="flex items-center justify-between rounded-xl border border-white/5 bg-white/[0.02] px-4 py-3 text-xs">
                <div>
                  <span className="font-bold">{s.guest?.name || "Guest"}</span>
                  <span className="ml-2 text-neon-violet">{s.guest?.handle}</span>
                  <span className="ml-2 text-zinc-500">→ {s.venue_name} · {s.entry_point}</span>
                </div>
                <div className="text-right text-zinc-500">
                  {s.amount_paid > 0 && <span className="mr-2 font-bold text-neon-green">${s.amount_paid}</span>}
                  {new Date(s.timestamp).toLocaleString()}
                </div>
              </div>
            ))}
            {scans.length === 0 && <p className="py-8 text-center text-sm text-zinc-500">No scans yet.</p>}
          </div>
        )}
      </main>
    </div>
  );
}
